import React, { useEffect, useRef, useCallback, useState } from 'react'
import { Box, Typography, Fade, Portal } from '@mui/material'
import { useSwipeable } from 'react-swipeable'
import { useHaptic } from '../../../hooks/useHaptic'

/**
 * Menu item configuration
 */
export interface MenuItem {
  /** Unique identifier */
  id: string

  /** Display label */
  label: string

  /** Icon component */
  icon: React.ComponentType<{ size?: string | number }>

  /** Click handler */
  onClick: () => void

  /** Visual variant */
  variant?: 'default' | 'destructive'
}

/**
 * ContextMenu props
 */
export interface ContextMenuProps {
  /** Whether the menu is open */
  open: boolean

  /** Callback when menu should close */
  onClose: () => void

  /** Menu items */
  items: MenuItem[]

  /** Optional title for the menu */
  title?: string
}

/**
 * ContextMenu Component
 *
 * Mobile-optimized context menu displayed as a bottom sheet.
 * Supports keyboard navigation, swipe-to-dismiss, and accessibility.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false)
 *
 * <ContextMenu
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   items={[
 *     { id: 'edit', label: 'Edit', icon: Edit, onClick: handleEdit },
 *     { id: 'delete', label: 'Delete', icon: Trash2, onClick: handleDelete, variant: 'destructive' },
 *   ]}
 * />
 * ```
 */
export function ContextMenu({
  open,
  onClose,
  items,
  title,
}: ContextMenuProps): JSX.Element | null {
  const menuRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState(0)

  const { light } = useHaptic()

  /**
   * Handle item click
   */
  const handleItemClick = useCallback(
    (item: MenuItem) => {
      light()
      item.onClick()
      onClose()
    },
    [light, onClose]
  )

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          const nextIndex = Math.min(index + 1, items.length - 1)
          setFocusedIndex(nextIndex)
          itemRefs.current[nextIndex]?.focus()
          break

        case 'ArrowUp':
          e.preventDefault()
          const prevIndex = Math.max(index - 1, 0)
          setFocusedIndex(prevIndex)
          itemRefs.current[prevIndex]?.focus()
          break

        case 'Enter':
        case ' ':
          e.preventDefault()
          handleItemClick(items[index])
          break

        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [items, handleItemClick, onClose]
  )

  /**
   * Handle Escape key at document level
   */
  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  /**
   * Focus first item when menu opens
   */
  useEffect(() => {
    if (open && itemRefs.current[0]) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        itemRefs.current[0]?.focus()
        setFocusedIndex(0)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  /**
   * Swipe handlers for dismiss
   */
  const swipeHandlers = useSwipeable({
    onSwipedDown: (eventData) => {
      if (eventData.deltaY > 100) {
        onClose()
      }
    },
    trackTouch: true,
    trackMouse: false,
  })

  if (!open) {
    return null
  }

  return (
    <Portal>
      {/* Backdrop */}
      <Fade in={open}>
        <Box
          data-testid="context-menu-backdrop"
          onClick={onClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
          }}
        />
      </Fade>

      {/* Menu */}
      <Fade in={open}>
        <Box
          {...swipeHandlers}
          ref={menuRef}
          role="menu"
          aria-label="Context menu"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.paper',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 1301,
            maxHeight: '70vh',
            overflow: 'auto',
            pb: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {/* Drag handle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1.5,
              pb: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                backgroundColor: 'grey.300',
              }}
            />
          </Box>

          {/* Title */}
          {title && (
            <Typography
              data-testid="context-menu-title"
              variant="subtitle1"
              sx={{
                px: 2,
                pb: 1,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          )}

          {/* Menu items */}
          <Box sx={{ px: 1, pb: 2 }}>
            {items.map((item, index) => {
              const Icon = item.icon
              const isDestructive = item.variant === 'destructive'

              return (
                <Box
                  key={item.id}
                  component="button"
                  ref={(el: HTMLButtonElement | null) => {
                    itemRefs.current[index] = el
                  }}
                  role="menuitem"
                  tabIndex={focusedIndex === index ? 0 : -1}
                  data-variant={item.variant || 'default'}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    width: '100%',
                    px: 2,
                    py: 1.5,
                    border: 'none',
                    borderRadius: 2,
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: isDestructive ? 'error.main' : 'text.primary',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: isDestructive
                        ? 'error.light'
                        : 'action.hover',
                    },
                    '&:focus': {
                      outline: 'none',
                      backgroundColor: isDestructive
                        ? 'error.light'
                        : 'action.selected',
                    },
                    '&:active': {
                      backgroundColor: isDestructive
                        ? 'error.light'
                        : 'action.selected',
                    },
                  }}
                >
                  <Icon size={20} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: 'inherit',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Fade>
    </Portal>
  )
}
